<template>
  <template
    v-for="({ name, display_name, prices: priceGroups, description }, resaleAddonIndex) in resaleAddons"
    :key="resaleAddonIndex"
  >
    <div
      v-if="addonHasFillablePrices(priceGroups)"
      class="addon card"
    >
      <div class="title">
        <!-- TODO: Refine clickable area -->
        <div
          class="select_type"
          @click="toggleAddonByPriceGroups(priceGroups)"
        >
          <span
            :class="[
              'temp_radio',
              isResaleAddonEnabled(priceGroups) ? 'active' : ''
            ]"
          >
            <svg-vue :icon="isResaleAddonEnabled(priceGroups) ? 'icons/checkmark' : 'icons/plus'" />
          </span>
        </div>
        <div class="minimise">
          <h3 @click="toggleAddonByPriceGroups(priceGroups)">
            {{ display_name ?? name }}<br>
          </h3>
          <small v-html="description ?? ''" />
        </div>
      </div>
      <accordion-transition>
        <div
          v-if="isResaleAddonEnabled(priceGroups)"
          class="height_transition"
        >
          <div
            v-for="(individualPrices, groupName) in priceGroups"
            :key="groupName"
            class="price"
          >
            <h4
              v-if="groupName !== 'Price'"
              v-html="groupName"
            />

            <template
              v-for="(individualPrice, priceIndex) in individualPrices"
              :key="priceIndex"
            >
              <product-currency-input
                v-if="shouldShowInput(individualPrice)"
                :price-in-pence="userAddons[individualPrice.stripe_id] ?? 0"
                :placeholder="placeholderMessage(individualPrice)"
                :message="minimumMessage(individualPrice)"
                :currency="individualPrice.currency"
                :currency-symbol="currencySymbol(individualPrice)"
                :minimum-price-data="minimumPriceData(individualPrice)"
                :error-string="errors[`addons.${individualPrice.stripe_id}`]"
                :message-popup-content="addonMinimumPopupContent"
                @change="(value) => setAddonPrice({
                  stripeId: individualPrice.stripe_id,
                  price: value,
                })"
              />
            </template>
          </div>
        </div>
      </accordion-transition>
    </div>
  </template>
  <button
    v-if="!hideButton"
    class="button"
    :disabled="loading"
    @click="validateAndSubmit"
  >
    <i
      v-if="loading"
      class="fas fa-spin fa-spinner"
    />
    Continue
  </button>
</template>

<script>
import AccordionTransition from "../../../../views/reports/components/global/transitions/AccordionTransition";
import ProductCurrencyInput from "../setupWizardSteps/includes/ProductCurrencyInput";
import _ from "lodash";
import {mapActions, mapGetters} from "vuex";
import Swal from "sweetalert2";
import {isAddonPriceRequired} from '../../../modules/merchantCentre/addonManager/methods';

export default {
  components: {
    ProductCurrencyInput,
    AccordionTransition,
  },
  props: {
    userAddons: {
      type: Object,
      required: true,
    },
    useCards: {
      type: Boolean,
      default: false,
    },
    hideButton: {
      type: Boolean,
      default: false,
    },
  },
  emits: [
    'submit',
  ],
  computed: {
    ...mapGetters('productManager', [
      'softwareProduct',
      'supportedCurrencies',
      'loading',
    ]),
    ...mapGetters('addonManager', [
      'resaleAddons',
      'addons',
      'addonsByStripeId',
      'resaleAddonPricesByStripeId',
      'errors',
    ]),
    addonMinimumPopupContent() {
      return `
        <h1>How we calculate minimum price</h1>
        <div style="font-size: 1rem;">
          <p style="line-height: 1.4rem;">
            Our minimum price is based on the cost you pay for the Hike add-ons (including taxes, if applicable),
            plus Stripes payment handling fees (3% + a single 0.20 (£/$) charge).
          </p>
          <p style="line-height: 1.4rem;">
            So, to put it simply:<br>
            Hike add-on cost + Stripe fees = your minimum price
          </p>
          <p style="line-height: 1.4rem;">
            These costs are calculated and deducted automatically via Stripe, so you don't have to worry about
            accounting for these charges within your pricing.
          </p>
          <p style="line-height: 1.4rem;">
            Please note; our charges will not be visible to your customers
          </p>
          <p style="line-height: 1.4rem;">
            A minimum price of $1/£1 is required to ensure successful product creation within Stripe.
          </p>
        </div>
      `;
    },
  },
  async created() {
    // TODO: Before leaving, warn users of lost data - unless saving
    await this.fetchResaleAddons();
    Object.values(this.resaleAddons).forEach(({prices: priceGroup}) => {
      if (this.isResaleAddonEnabled(priceGroup)) {
        let enabledCurrencies = Object.keys(this.supportedCurrencies).filter((currency) => {
          return this.supportedCurrencies[currency].enabled === true
        });
        Object.values(priceGroup)
          .reduce((prices, item) => [...prices, ...item], [])
          .forEach(({stripe_id, currency}) => {
              if (
                !this.addons[stripe_id]
                && enabledCurrencies.includes(currency)
              ) {
                this.setAddonPrice({
                  stripeId: stripe_id,
                  price: 0,
                });
              }
          });

      }
    });
  },
  methods: {
    ...mapActions('addonManager', [
      'fetchResaleAddons',
      'enableAddonPriceIfRequired',
      'removeAddonByStripeId',
      'setAddonPrice',
      'validateAddons',
    ]),
    shouldShowInput(resaleAddonPrice) {
      return isAddonPriceRequired(
        this.supportedCurrencies,
        this.softwareProduct.prices,
        resaleAddonPrice,
      );
    },
    addonHasFillablePrices(priceGroups) {
      return Object.values(priceGroups) // [price, price]
        .some((prices) =>
          prices.some(individualPrice => this.shouldShowInput(individualPrice))
        );
    },
    isResaleAddonEnabled(priceGroups) {
      Object.values(priceGroups)
        .some((prices) => {
          return prices.some(({stripe_id}) => {
            return this.userAddons[stripe_id] !== undefined
          })
        })
      return Object.values(priceGroups)
        .some((prices) =>
          prices.some(({stripe_id}) => this.userAddons[stripe_id] !== undefined)
        );
    },
    toggleAddonByPriceGroups(priceGroups) {
      _.forEach(priceGroups, priceGroup => {
        _.forEach(priceGroup, individualPrice => {

          if (this.userAddons[individualPrice.stripe_id] === undefined) {
            this.enableAddonPriceIfRequired(individualPrice);
          } else {
            this.removeAddonByStripeId(individualPrice.stripe_id);
          }

        });
      });
    },
    currencySymbol(price) {
      return price.currency === 'GBP' ? '&pound;' : '&dollar;';
    },
    minimumMessage(price) {
      const message = [
        price.minimum === 0 ? 'No minimum' : 'Minimum',
        price.billing_period === 'MONTH' ? 'monthly' : null,
        price.billing_period === 'YEAR' ? 'annual' : null,
        'price',
        price.billing_period === 'ONCE' ? '(one-time payment)' : null,
      ];
      return message.filter(price => price != null).join(' ');
    },
    placeholderMessage(price) {
      switch(price.billing_period) {
        case 'MONTH':
          return 'Monthly price';
        case 'YEAR':
          return 'Annual price';
        case 'ONCE':
          return 'One-time price';
      }
    },
    validateAndSubmit() {
      this.validateAddons().then(() => {
        this.$emit('submit');
      }).catch(() => {
        Swal.fire({
          icon: "warning",
          title: "Whoops!",
          text: 'Sorry, there were some problems with your submission. '+
            'Please see the highlighted fields for more information.',
        })
      });
    },
    minimumPriceData(individualPrice) {
      const {price, minimum, stripe_fees} = individualPrice;
      return {price, minimum, stripe_fees};
    }
  }
}
</script>

<style lang="scss" scoped>
.addon {
  padding: 1rem;
  background: #FBFAFA;

  .price {
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    grid-column-gap: 16px;

    h4 {
      font-size: 1.125rem;
      line-height: 1.625rem;
      font-weight: 500;
      grid-column: 1 / 3;
    }
  }

  .currency_input {
    margin-bottom: 1rem;
  }
}

.title {
  display: flex;
  align-items: flex-start;
  padding-bottom: .5rem;
}

h3 {
  font-size: 1.5rem;
  line-height: 1.3rem;
  font-weight: 700;
  margin: 0;

  small {
    font-size: 0.75rem;
  }
}

.form-control {
  margin-top: 0;
}

.select_type {
  padding: 0;
}

.minimise {
  h3 {
    cursor: pointer;
  }

  small {
    line-height: 1.1rem;
    display: inline-block;
    margin-top: 0.5rem;
  }
}

.price_block {
  display: grid;
  border-radius: 6px;
  margin: 1rem 0;
  padding: 1.5rem;
  background: #FBFAFA;

  grid-template-columns: repeat(2, 1fr);
  grid-template-rows: repeat(2, 1fr);
  grid-column-gap: 0;
  grid-row-gap: 0;

  > h4 {
    font-size: 1.125rem;
    font-weight: 500;
  }
}

.temp_radio {
  padding: 8px;
  border: 1px solid #37B2A8;
  cursor: pointer;
  margin-right: 15px;
  width: 32px;
  height: 32px;
  color: white;
  border-radius: 6px;
  background: #fff;
  display: flex;

  svg :deep(path) {
    stroke: #37B2A8;
  }

  &.active {
    background: #37B2A8;
    border: 0;

    svg :deep(path) {
      stroke: #FFFFFF;
    }
  }
}

.button {
  background: #37B2A8;
  border-radius: 12px;
  width: 100%;
  padding: 1rem;
  color: #fff;
  font-size: 1rem;
  font-weight: 700;
}

.height_transition {
  transition: height 0.3s ease;
}
</style>
